import { FC, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Breadcrumbs,
  Link,
  IconButton,
  Typography,
  InputAdornment,
  Tooltip,
  Avatar,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { Field } from "formik";
import "react-phone-number-input/style.css";
import { Formik } from "formik";
import { useTheme } from "@material-ui/core/styles";
import ChevronRightIcon from "src/icons/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import PencilAltIcon from "../../icons/PencilAlt";
import useAuth from "../../hooks/useAuth";
import * as Yup from "yup";
import {
  getConfigUsersById,
  updateConfigUser,
} from "src/pages/Profile/ConnectionService";
import AlertMessage from "src/components/ui-components/AlertMessage";
import InputField from "src/components/ui-components/InputField";
import PhoneInput from "src/components/shared/PhoneInput";
import {
  parsePhoneNumber,
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import SaveIcon from "@material-ui/icons/Save";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const defaultForm = {
  fullName: "",
  email: "",
  mobile: "",
  userName: "",
  submit: null,
};

export const UserProfile: FC = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, resetPassword } = useAuth() as any;
  const [editMode, setEditMode] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [changePasswordEditMode, setChangePasswordEditMode] = useState(false);
  const childRef = useRef(null);
  const changePasswordRef = useRef(null);
  const userId = JSON.parse(localStorage.getItem("userDetails"))?.configUserKey;
  const { email, username } = JSON.parse(localStorage.getItem("userDetails"));

  const [passwordVisibility, setPasswordVisibility] = useState<any>({
    password: "",
    showOldPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });

  const handleClick = () => {
    if (editMode) {
      submitForm();
    } else {
      setEditMode(true);
      changePasswordRef.current.resetForm();
      setChangePasswordEditMode(false);
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const submitForm = () => {
    if (childRef && childRef.current && !childRef.current.isSubmitting) {
      childRef.current.submitForm();
    }
  };
  const theme = useTheme();

  let setForm: any;
  const setFormData = (data: any) => {
    data = Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key] === null ? "" : data[key];
      return acc;
    }, {});
    setForm({
      values: {
        email: data.email,
        mobile: data.mobile,
        userName: data.username,
        fullName: data.fullName,
      },
    });
  };

  const getUserDetail = () => {
    try {
      getConfigUsersById(userId.toString()).then((res) => {
        setFormData(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  useEffect(() => {
    getUserDetail();
  });

  return (
    <Box>
      <Container maxWidth={false}>
        <Helmet>
          <title>User Profile</title>
        </Helmet>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            opacity: 1,
            width: "100%",
            py: 1,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Grid
            container
            alignItems="center"
            sx={{ my: 2 }}
            justifyContent="space-between"
          >
            <Grid item>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to=""
                  variant="h5"
                >
                  Profile
                </Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            backgroundColor: "background.default",
            minHeight: "100%",
            py: 0,
          }}
        ></Box>
        <Box>
          <Grid item md={12} xs={12}>
            <Grid container spacing={3}>
              <Grid item md={8} xs={12}>
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title="User Details"
                    action={
                      <Tooltip title={editMode ? "Save" : "Edit"}>
                        <IconButton
                          onClick={handleClick}
                          aria-label={editMode ? "save" : "edit"}
                        >
                          {editMode ? (
                            <SaveIcon fontSize="small" />
                          ) : (
                            <PencilAltIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider sx={{ mx: 2, marginBottom: 1, marginTop: 1 }} />
                  <Formik
                    enableReinitialize
                    innerRef={childRef}
                    initialValues={defaultForm}
                    validationSchema={Yup.object({
                      fullName: Yup.string()
                        .max(100)
                        .required("Full Name is required")
                        .test("regex", "Full Name is not valid", (val) => {
                          const regExp = new RegExp(
                            "^[a-zA-Z]+([\\s][a-zA-Z]+)*$"
                          );
                          return regExp.test(val);
                        }),
                      userName: Yup.string()
                        .nullable()
                        .max(100, 'Username must not exceed 100 characters')
                        .min(5, 'Username must be at least 5 characters long')
                        .matches(/^[a-zA-z0-9]*$/u, 'Please enter a valid username')
                        .matches(/[a-zA-Z]/, 'Username should be combination of alphanumeric characters')
                        .required('Username is required'),
                      mobile: Yup.string()
                        .required("Mobile number is required")
                        .test(
                          'phoneValidation',
                          'Must be a valid phone number',
                          (value, context) => {
                            if (!value) return true;
                            const phoneNumber = parsePhoneNumberFromString(value);
                            if (phoneNumber && phoneNumber.isValid()) {
                              return true;
                            } else {
                              return false;
                            }
                          }
                        )
                    })}
                    onSubmit={async (values): Promise<void> => {
                      setChangePasswordEditMode(false);
                      if (editMode) {
                        try {
                          const updatdUser = await updateConfigUser({
                            configUserKey: userId,
                            fullName: values.fullName,
                            userName: values.userName,
                            mobile: values.mobile,
                          });
                          if (updatdUser.isSuccess === true) {
                            AlertMessage({
                              type: "success",
                              msg: "Profile updated successfully",
                            });
                            var userDetailJSON = JSON.parse(
                              localStorage.getItem("userDetails")
                            );
                            userDetailJSON.fullName = values.fullName;
                            localStorage.setItem(
                              "userDetails",
                              JSON.stringify(userDetailJSON)
                            );
                            setTimeout(() => {
                              window.location.reload();
                            }, 2000);
                          }
                        } catch (err) {
                          if (err && err.errors) {
                            Object.entries(err.errors).forEach(([, errMsg]) => {
                              AlertMessage({
                                type: "error",
                                msg: "Something went wrong",
                              });
                            });
                          } else {
                            AlertMessage({
                              type: "error",
                              msg: err.title || err.message,
                            });
                          }
                        }
                        setEditMode(false);
                      }
                    }}
                  >
                    {({
                      errors,
                      resetForm,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      touched,
                      values,
                    }): JSX.Element => {
                      setForm = resetForm;
                      return (
                        <>
                          <form
                            noValidate
                            onSubmit={handleSubmit}
                            {...props}
                            autoComplete="off"
                          >
                            <CardContent>
                              <Grid container spacing={3}>
                                <Grid item md={4} xs={12}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      p: 3,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: 148,
                                        width: 148,
                                        borderRadius: "50%",
                                        backgroundColor:
                                          theme.palette.primary.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Avatar
                                        src={user.avatar}
                                        sx={{
                                          height: 150,
                                          width: 150,
                                          color: "#ffffff",
                                          background:
                                            "linear-gradient(to bottom, #072f40, #072f40)",
                                        }}
                                      ></Avatar>
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      maxWidth: "100%",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ maxWidth: "100%" }}>
                                      {user.email}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item md={8} xs={12}>
                                  <Grid container spacing={2}>
                                    <Grid item md={12} xs={12}>
                                      <Box>
                                        <InputField
                                          disabled={!editMode}
                                          label="Full Name *"
                                          name="fullName"
                                        />
                                      </Box>
                                    </Grid>
                                    <Grid item md={12} xs={12}>
                                      <Field name="mobile" disabled={!editMode}>
                                        {({
                                          field,
                                          form: {
                                            errors,
                                            handleBlur,
                                            values,
                                            setErrors,
                                            touched,
                                          },
                                          meta,
                                        }) => (
                                          <PhoneInput
                                            label="Mobile *"
                                            id="phoneInput"
                                            name="mobile"
                                            {...field}
                                            disabled={!editMode}
                                            onBlur={(e) => {
                                              setPhoneTouched(true);
                                              handleBlur(e);
                                            }}
                                            value={values.mobile}
                                            error={
                                              touched.mobile &&
                                              Boolean(meta.error)
                                            }
                                            helperText={
                                              touched.mobile && meta.error
                                            }
                                            onChange={(mob: string): any => {
                                              values.mobile = mob || "";
                                              if (
                                                isPossiblePhoneNumber(
                                                  values.mobile
                                                )
                                              ) {
                                                const phoneNumber =
                                                  parsePhoneNumber(
                                                    values.mobile
                                                  );
                                                if (phoneNumber) {
                                                  setErrors({
                                                    ...errors,
                                                    mobile: undefined,
                                                  });
                                                }
                                              } else {
                                                if (phoneTouched) {
                                                  meta.touched = true;
                                                }
                                                setErrors({
                                                  ...errors,
                                                  mobile:
                                                    "Must be a valid phone number",
                                                });
                                              }
                                            }}
                                          />
                                        )}
                                      </Field>
                                    </Grid>

                                    <Grid item md={12} xs={12}>
                                      <Box>
                                        <TextField
                                          disabled={true}
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.email}
                                          name="email"
                                          error={Boolean(
                                            touched.email && errors.email
                                          )}
                                          helperText={
                                            touched.email && errors.email
                                          }
                                          fullWidth
                                          label="Email"
                                          variant="outlined"
                                        />
                                      </Box>
                                    </Grid>
                                    <Grid item md={12} xs={12}>
                                      <Box>
                                        <TextField
                                          disabled={!editMode}
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.userName}
                                          name="userName"
                                          error={Boolean(
                                            touched.userName && errors.userName
                                          )}
                                          helperText={
                                            touched.userName && errors.userName
                                          }
                                          fullWidth
                                          label="User Name *"
                                          variant="outlined"
                                        />
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </form>
                        </>
                      );
                    }}
                  </Formik>
                </Card>
              </Grid>
              <Grid item md={4} xs={12}>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                    <Formik
                      enableReinitialize
                      innerRef={changePasswordRef}
                      initialValues={{
                        oldPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      }}
                      validationSchema={Yup.object().shape({
                        oldPassword: Yup.string()
                          .min(8, "Old Password must be at least 8 characters")
                          .required("Old password is required"),
                        newPassword: Yup.string()
                          .required('Password is required')
                          .min(8, 'Password must be at least 8 characters long')
                          .max(20, 'Password must not exceed 20 characters')
                          .matches(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
                          .matches(/[a-z]/, 'Password must contain at least one lowercase letter (a-z)')
                          .matches(/[0-9]/, 'Password must contain at least one number (0-9)')
                          .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character (e.g., !, @, #, $, etc.)')
                          .matches(/^\S*$/, 'Password must not contain any spaces')
                          .test('no-sequences', 'Password must not contain sequences (e.g., 1234, abcd) or repeated characters (e.g., aaa)', value => {
                            const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(value);
                            const hasRepeated = /(.)\1\1/.test(value);
                            return !hasSequence && !hasRepeated;
                          })
                          .test('different-from-old-password', 'New password must be different from old password', function (value) {
                            return value !== this.parent.oldPassword;
                          })
                          .test('no-username-email', 'Password must not contain parts of the username or email', function (value) {
                            if (!value) return true;
                            if (!username || !email) return true;
                            const lowerCaseValue = value.toLowerCase();
                            const lowerCaseUsername = username.toLowerCase();
                            const emailUsernamePart = email.split('@')[0].toLowerCase();
                            const containsSubstring = (str, substrLength) => {
                              for (let i = 0; i <= str.length - substrLength; i++) {
                                if (lowerCaseValue.includes(str.slice(i, i + substrLength))) {
                                  return true;
                                }
                              }
                              return false;
                            };
                            return !(containsSubstring(lowerCaseUsername, 5) || containsSubstring(emailUsernamePart, 5));
                          }),
                        confirmPassword: Yup.string()
                          .required("Please confirm your password")
                          .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
                      })}
                      onSubmit={async (
                        sbmitValues,
                        { resetForm: cpResetForm }
                      ): Promise<void> => {
                        if (isSubmitting) return;

                        setIsSubmitting(true);
                        try {
                          await resetPassword({
                            ...sbmitValues,
                            userEmail: email,
                          });
                          AlertMessage({
                            type: "success",
                            msg: "Password changed successfully",
                          });
                          cpResetForm();
                          setChangePasswordEditMode(false);
                        } catch (err) {
                          if (
                            err &&
                            err.errors &&
                            err.errors.DatabaseValidation
                          ) {
                            Object.entries(err.errors).forEach(([, errMsg]) => {
                              AlertMessage({
                                type: "error",
                                msg: "Something went wrong",
                              });
                            });
                          } else {
                            AlertMessage({
                              type: "error",
                              msg: err.title,
                            });
                          }
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      {({
                        touched: cpTouched,
                        errors: cpErrors,
                        values: cpValues,
                        handleSubmit: cpHandleSubmit1,
                        // setFieldValue: cpSetFieldValue,
                        handleChange: cpHandleChange,
                        handleBlur: cpHandleBlur,
                      }): JSX.Element => {
                        return (
                          <form
                            noValidate
                            onSubmit={cpHandleSubmit1}
                            {...props}
                            autoComplete="off"
                          >
                            <Card sx={{ height: "100%" }}>
                              <CardHeader
                                title="Change Password"
                                action={
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={() =>
                                        setChangePasswordEditMode(true)
                                      }
                                      aria-label="settings"
                                    >
                                      {/* <Edit /> */}
                                      <PencilAltIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                }
                              />
                              <Divider sx={{ mx: 2, paddingTop: 1 }} />
                              <CardContent>
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{ paddingTop: "2%", paddingBottom: "2%" }}
                                >
                                  <Grid item md={12} xs={12}>
                                    <TextField
                                      disabled={!changePasswordEditMode}
                                      label="Old Password"
                                      onBlur={cpHandleBlur}
                                      onChange={cpHandleChange}
                                      type={
                                        passwordVisibility.showOldPassword
                                          ? "text"
                                          : "password"
                                      }
                                      value={cpValues.oldPassword}
                                      name="oldPassword"
                                      error={Boolean(
                                        cpTouched.oldPassword &&
                                        cpErrors.oldPassword
                                      )}
                                      helperText={
                                        cpTouched.oldPassword &&
                                        cpErrors.oldPassword
                                      }
                                      fullWidth
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              disabled={!changePasswordEditMode}
                                              aria-label="toggle password visibility"
                                              onClick={() => {
                                                setPasswordVisibility({
                                                  ...passwordVisibility,
                                                  showOldPassword:
                                                    !passwordVisibility.showOldPassword,
                                                });
                                              }}
                                              onMouseDown={
                                                handleMouseDownPassword
                                              }
                                              edge="end"
                                            >
                                              {passwordVisibility.showOldPassword ? (
                                                <VisibilityOff />
                                              ) : (
                                                <Visibility />
                                              )}
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                        autoComplete: "off",
                                      }}
                                      variant="outlined"
                                    />
                                  </Grid>
                                  <Grid item md={12} xs={12}>
                                    <TextField
                                      disabled={!changePasswordEditMode}
                                      label="New Password"
                                      onBlur={cpHandleBlur}
                                      onChange={cpHandleChange}
                                      onCut={(e) => e.preventDefault()}
                                      onCopy={(e) => e.preventDefault()}
                                      type={
                                        passwordVisibility.showNewPassword
                                          ? "text"
                                          : "password"
                                      }
                                      value={cpValues.newPassword}
                                      name="newPassword"
                                      error={Boolean(
                                        cpTouched.newPassword &&
                                        cpErrors.newPassword
                                      )}
                                      helperText={
                                        cpTouched.newPassword &&
                                        cpErrors.newPassword
                                      }
                                      fullWidth
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              disabled={!changePasswordEditMode}
                                              aria-label="toggle password visibility"
                                              onClick={() => {
                                                setPasswordVisibility({
                                                  ...passwordVisibility,
                                                  showNewPassword:
                                                    !passwordVisibility.showNewPassword,
                                                });
                                              }}
                                              onMouseDown={
                                                handleMouseDownPassword
                                              }
                                              edge="end"
                                            >
                                              {passwordVisibility.showNewPassword ? (
                                                <VisibilityOff />
                                              ) : (
                                                <Visibility />
                                              )}
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                      variant="outlined"
                                    />
                                  </Grid>
                                  <Grid item md={12} xs={12}>
                                    <TextField
                                      disabled={!changePasswordEditMode}
                                      label="Confirm Password"
                                      onBlur={cpHandleBlur}
                                      onChange={cpHandleChange}
                                      onCut={(e) => e.preventDefault()}
                                      onCopy={(e) => e.preventDefault()}
                                      onPaste={(e) => e.preventDefault()}
                                      type={
                                        passwordVisibility.showConfirmPassword
                                          ? "text"
                                          : "password"
                                      }
                                      value={cpValues.confirmPassword}
                                      name="confirmPassword"
                                      error={Boolean(
                                        cpTouched.confirmPassword &&
                                        cpErrors.confirmPassword
                                      )}
                                      helperText={
                                        cpTouched.confirmPassword &&
                                        cpErrors.confirmPassword
                                      }
                                      fullWidth
                                      variant="outlined"
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    md={12}
                                    xs={12}
                                    sx={{
                                      paddingBottom: "10px",
                                    }}
                                  >
                                    <Button
                                      disabled={!changePasswordEditMode}
                                      variant="contained"
                                      onClick={() => {
                                        cpHandleSubmit1();
                                      }}
                                      autoFocus
                                      sx={{
                                        backgroundColor:
                                          "linear-gradient(to bottom, #072f40, #072f40)",
                                        "&:hover": {
                                          backgroundColor: "#072f40",
                                        },
                                        width: "150px",
                                        height: "35px",
                                        fontSize: "18px",
                                      }}
                                    >
                                      Change Password
                                    </Button>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </form>
                        );
                      }}
                    </Formik>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default UserProfile;
